import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import TopNavigation from '../../components/feature/TopNavigation';
import { paidApi, type PaidListItem } from '../../services/api';

export default function SettlementPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'pending' | 'completed'>('pending');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // API 응답 데이터
  const [totalPaid, setTotalPaid] = useState(0); // 총 정산 완료 금액
  const [allSettlements, setAllSettlements] = useState<PaidListItem[]>([]); // 전체 목록
  const [page, setPage] = useState(1);
  const [totalCnt, setTotalCnt] = useState(0);
  const perPageNum = 20;

  // 데이터 로딩
  const loadSettlements = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      const response = await paidApi.getPaidList({
        memberNo: user.memberNo,
        apiKey: user.apiKey,
        page,
        perPageNum,
      });

      setTotalPaid(response.totalPaid);
      setAllSettlements(response.paidList);
      setTotalCnt(response.pagingList.totalCnt);
    } catch (err: any) {
      console.error('정산 목록 조회 오류:', err);
      setError(err.message || '정산 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettlements();
  }, [user, page]);

  // 정산 예정 목록 (PAID_YN = 0)
  const pendingSettlements = allSettlements.filter(s => s.paidYn === 0);

  // 정산 완료 목록 (PAID_YN = 1)
  const completedSettlements = allSettlements.filter(s => s.paidYn === 1);

  const formatCurrency = (amount: number) => {
    return amount.toLocaleString('ko-KR');
  };

  const handleDetailClick = (paidNo: number) => {
    navigate(`/settlement/${paidNo}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-3xl text-purple-500 animate-spin"></i>
          <p className="mt-2 text-sm text-gray-600">로딩 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 pb-20">
        <div className="bg-white px-4 py-4 flex items-center sticky top-0 z-10 shadow-sm">
          <button onClick={() => navigate(-1)} className="mr-3">
            <i className="ri-arrow-left-line text-xl"></i>
          </button>
          <h1 className="text-lg font-semibold">정산관리</h1>
        </div>
        <div className="px-4 py-8">
          <div className="bg-white rounded-2xl p-8 text-center">
            <i className="ri-error-warning-line text-4xl text-red-500 mb-3"></i>
            <p className="text-gray-700 mb-2">{error}</p>
            <button
              onClick={loadSettlements}
              className="mt-4 px-6 py-2 bg-purple-500 text-white rounded-lg text-sm"
            >
              다시 시도
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* 헤더 */}
      <TopNavigation
        title="정산관리"
        showBackButton
        onBackClick={() => navigate(-1)}
      />

      {/* 탭 (고정 헤더 h-16 아래로 내려 겹침/클릭 가로채짐 방지) */}
      <div className="px-4 pt-20 pb-4">
        <div className="bg-white rounded-xl p-1 flex shadow-sm">
          <button
            onClick={() => setActiveTab('pending')}
            className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${activeTab === 'pending'
              ? 'bg-purple-500 text-white shadow-sm'
              : 'text-gray-600'
              }`}
          >
            정산 예정 ({pendingSettlements.length})
          </button>
          <button
            onClick={() => setActiveTab('completed')}
            className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${activeTab === 'completed'
              ? 'bg-purple-500 text-white shadow-sm'
              : 'text-gray-600'
              }`}
          >
            정산 완료 ({completedSettlements.length})
          </button>
        </div>
      </div>

      {/* 정산 예정 목록 */}
      {activeTab === 'pending' && (
        <div className="px-4 space-y-3">
          {pendingSettlements.length === 0 ? (
            <div className="bg-white rounded-2xl p-8 text-center">
              <i className="ri-file-list-3-line text-4xl text-gray-300 mb-3"></i>
              <p className="text-gray-500 text-sm">정산 예정 건이 없습니다</p>
            </div>
          ) : (
            pendingSettlements.map((settlement) => (
              <div
                key={settlement.paidNo}
                className="bg-white rounded-2xl p-4 shadow-sm cursor-pointer active:scale-98 transition-transform"
                onClick={() => handleDetailClick(settlement.paidNo)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-gray-900">
                        {settlement.name}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">
                      {settlement.startDate} ~ {settlement.endDate}
                    </div>
                  </div>
                  <div className="bg-yellow-50 text-yellow-600 text-xs px-2.5 py-1 rounded-full font-medium">
                    예정
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">총 간병비</span>
                    <span className="font-bold text-purple-600">
                      {formatCurrency(settlement.totalMoney)}원
                    </span>
                  </div>
                </div>

                <div className="mt-3 flex items-center justify-end text-xs text-gray-400">
                  <span>상세보기</span>
                  <i className="ri-arrow-right-s-line ml-1"></i>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* 정산 완료 목록 */}
      {activeTab === 'completed' && (
        <div className="px-4 space-y-3">
          <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">총 정산 완료 금액</span>
              <span className="text-xl font-bold text-purple-600">
                {formatCurrency(totalPaid)}원
              </span>
            </div>
          </div>

          {completedSettlements.length === 0 ? (
            <div className="bg-white rounded-2xl p-8 text-center">
              <i className="ri-file-list-3-line text-4xl text-gray-300 mb-3"></i>
              <p className="text-gray-500 text-sm">정산 완료 건이 없습니다</p>
            </div>
          ) : (
            completedSettlements.map((settlement) => (
              <div
                key={settlement.paidNo}
                className="bg-white rounded-2xl p-4 shadow-sm cursor-pointer active:scale-98 transition-transform"
                onClick={() => handleDetailClick(settlement.paidNo)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-gray-900">
                        {settlement.name}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">
                      {settlement.startDate} ~ {settlement.endDate}
                    </div>
                  </div>
                  <div className="bg-green-50 text-green-600 text-xs px-2.5 py-1 rounded-full font-medium">
                    완료
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">총 간병비</span>
                    <span className="font-bold text-purple-600">
                      {formatCurrency(settlement.totalMoney)}원
                    </span>
                  </div>
                </div>

                <div className="mt-3 flex items-center justify-end text-xs text-gray-400">
                  <span>상세보기</span>
                  <i className="ri-arrow-right-s-line ml-1"></i>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}